import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

export default function SEO({ 
  title, 
  description, 
  keywords,
  image = '/og-image.png',
  url,
  type = 'website',
  noindex = false 
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // Usar traducciones por defecto si no se proporcionan
  const seoTitle = title || t('seo.title');
  const seoDescription = description || t('seo.description');
  const seoKeywords = keywords || t('seo.keywords');
  const siteUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // Schema.org structured data para SaaS
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "AdPulse AI",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": seoDescription,
    "url": baseUrl,
    "author": {
      "@type": "Organization",
      "name": "AdPulse AI"
    },
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "89",
      "priceCurrency": "USD",
      "offerCount": "3"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Meta Ads Integration",
      "Google Ads Integration", 
      "TikTok Ads Integration",
      "Google Analytics 4 Integration",
      "Search Console Integration",
      "AI-Powered Insights with GPT-4o",
      "Automated PDF Reports",
      "Smart Alerts",
      "White Label Customization"
    ]
  };

  // FAQ structured data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": t('faq.q1'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.a1')
        }
      },
      {
        "@type": "Question", 
        "name": t('faq.q2'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.a2')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.q3'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.a3')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.q4'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.a4')
        }
      },
      {
        "@type": "Question",
        "name": t('faq.q5'),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t('faq.a5')
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Básico */}
      <html lang={currentLang} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical */}
      <link rel="canonical" href={siteUrl} />
      
      {/* Hreflang para SEO internacional */}
      <link rel="alternate" hrefLang="es" href={`${baseUrl}?lang=es`} />
      <link rel="alternate" hrefLang="en" href={`${baseUrl}?lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:locale" content={currentLang === 'es' ? 'es_ES' : 'en_US'} />
      <meta property="og:site_name" content="AdPulse AI" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={siteUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      
      {/* Adicionales */}
      <meta name="author" content="AdPulse AI" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="theme-color" content="#d946ef" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqStructuredData)}
      </script>
    </Helmet>
  );
}
